import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { formatElapsedSince } from '@/lib/format';
import { renderNotification } from '@/lib/notification-labels';

/** v0.4 §7 通知基盤 (L-1 in_app)。ヘッダーの通知ベル + 未読バッジ + 直近通知のドロップダウン。
 *  ポーリングは AI-OCR ジョブ一覧等 (pages/AiOcrPage.tsx) と同じ TanStack Query refetchInterval 方式。
 *  ドロップダウンの開閉・外側クリック検知は InheritanceSourceBadge / NodeMultiSelect と同じ手動実装
 *  パターンに揃える (このリポジトリには汎用 Popover コンポーネントが無いため)。 */

type NotificationItem = {
  recipient_id: string;
  notification_id: string;
  event_type_id: string;
  payload: Record<string, unknown>;
  read_at: string | null;
  created_at: string;
};

type NotificationsResponse = { notifications: NotificationItem[]; unread_count: number };

const DROPDOWN_LIMIT = 10;
const POLL_INTERVAL_MS = 45_000;

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data } = useQuery<NotificationsResponse>({
    queryKey: ['notifications', { unread: true }],
    queryFn: () => apiFetch<NotificationsResponse>('/notifications?unread=1'),
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: true,
  });

  const readMut = useMutation({
    mutationFn: (notificationId: string) => apiFetch(`/notifications/${notificationId}/read`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const unreadCount = data?.unread_count ?? 0;
  const items = (data?.notifications ?? []).slice(0, DROPDOWN_LIMIT);

  const handleItemClick = (item: NotificationItem) => {
    if (!item.read_at) readMut.mutate(item.notification_id);
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={unreadCount > 0 ? `通知 (未読${unreadCount}件)` : '通知'}
        className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-md text-chrome-text-sub hover:bg-chrome-raised hover:text-white focus-ring"
      >
        <span className="relative inline-flex">
          <Bell size={18} aria-hidden />
          {unreadCount > 0 && (
            <span
              aria-hidden
              className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-brand-red text-white text-[10px] font-bold leading-4 text-center tabular-nums"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </span>
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="通知一覧"
          className="absolute right-0 top-full z-50 mt-1 w-[320px] max-w-[calc(100vw-1.5rem)] rounded-lg border border-surface-border bg-white shadow-overlay overflow-hidden"
        >
          <div className="px-3 py-2 border-b border-surface-line flex items-center justify-between">
            <span className="text-sm font-semibold text-text-body">通知</span>
            {unreadCount > 0 && <span className="text-xs text-text-sub tabular-nums">未読 {unreadCount} 件</span>}
          </div>
          <div className="max-h-[360px] overflow-y-auto">
            {items.length === 0 ? (
              <p className="px-3 py-6 text-center text-xs text-text-sub">未読の通知はありません</p>
            ) : (
              <ul>
                {items.map((item) => {
                  const rendered = renderNotification(item.event_type_id, item.payload);
                  return (
                    <li key={item.recipient_id} className="border-b border-surface-line last:border-b-0">
                      <button
                        type="button"
                        onClick={() => handleItemClick(item)}
                        className="w-full text-left px-3 py-2.5 min-h-[44px] hover:bg-surface-sunken flex items-start gap-2"
                      >
                        <span className="mt-1.5 shrink-0 w-2 h-2 rounded-full bg-brand-red" aria-hidden />
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-semibold text-text-body break-words">{rendered.title}</span>
                          <span className="block text-xs text-text-sub break-words line-clamp-2 mt-0.5">{rendered.body}</span>
                          <span className="block text-[11px] text-text-muted mt-1">{formatElapsedSince(item.created_at)}</span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <div className="px-3 py-2 border-t border-surface-line bg-surface-sunken">
            <button
              type="button"
              onClick={() => { setOpen(false); navigate('/notifications'); }}
              className="w-full text-center text-sm text-brand-red font-semibold hover:underline min-h-[44px]"
            >
              すべて見る
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
